import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, Users, ClipboardList, FileBarChart, LogOut, ExternalLink, Settings, Wallet, HandCoins, CalendarDays, Package, ChefHat, TrendingUp, PartyPopper, IndianRupee, Coffee, CalendarRange, ClipboardCheck } from "lucide-react";
import Logo from "@/components/Logo";
import MaintenanceBanner from "@/components/MaintenanceBanner";

const ALL_NAV = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true, testId: "nav-overview" },
  { to: "/dashboard/employees", label: "Employees", icon: Users, testId: "nav-employees" },
  { to: "/dashboard/attendance", label: "Attendance", icon: ClipboardList, testId: "nav-attendance" },
  { to: "/dashboard/leaves", label: "Leaves", icon: CalendarDays, testId: "nav-leaves" },
  { to: "/dashboard/parties", label: "Parties", icon: PartyPopper, testId: "nav-parties" },
  { to: "/dashboard/sales", label: "Sales", icon: IndianRupee, testId: "nav-sales" },
  { to: "/dashboard/duty", label: "Duty", icon: CalendarRange, testId: "nav-duty" },
  { to: "/dashboard/tasks", label: "Tasks", icon: ClipboardCheck, testId: "nav-tasks" },
  { to: "/dashboard/breaks", label: "Breaks", icon: Coffee, testId: "nav-breaks" },
  { to: "/dashboard/inventory", label: "Inventory", icon: Package, testId: "nav-inventory" },
  { to: "/dashboard/recipes", label: "Recipes", icon: ChefHat, testId: "nav-recipes" },
  { to: "/dashboard/menu-margin", label: "Menu margin", icon: TrendingUp, testId: "nav-menu-margin", ownerOnly: true },
  { to: "/dashboard/expenses", label: "Expenses", icon: Wallet, testId: "nav-expenses" },
  { to: "/dashboard/payments", label: "Payments", icon: HandCoins, testId: "nav-payments" },
  { to: "/dashboard/reports", label: "Reports", icon: FileBarChart, testId: "nav-reports" },
  { to: "/dashboard/settings", label: "Settings", icon: Settings, testId: "nav-settings" },
];

// Head chef only sees Overview + Recipes + Settings
const HEAD_CHEF_NAV_KEYS = new Set(["/dashboard", "/dashboard/recipes", "/dashboard/settings"]);

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const doLogout = async () => {
    await logout();
    nav("/login");
  };

  const NAV = user?.role === "head_chef"
    ? ALL_NAV.filter((n) => HEAD_CHEF_NAV_KEYS.has(n.to))
    : ALL_NAV.filter((n) => !n.ownerOnly || user?.role === "admin");

  return (
    <div className="min-h-screen bg-background flex" data-testid="dashboard-layout">
      <MaintenanceBanner />
      {/* Sidebar */}
      <aside className="hidden md:flex w-72 flex-col border-r border-border bg-white">
        <div className="px-8 py-10">
          <div className="flex items-center gap-3">
            <Logo variant="mark" className="w-10 h-10" />
            <div>
              <div className="text-[10px] tracking-[0.35em] text-muted-foreground uppercase">Royal Wow Meals</div>
              <div className="font-serif text-xl tracking-tight">Manager</div>
            </div>
          </div>
        </div>

        <nav className="px-4 flex-1 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-full text-sm transition-colors ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-foreground/70 hover:bg-muted"
                }`
              }
            >
              <n.icon className="w-4 h-4" aria-hidden="true" />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-border">
          <a
            href="/kiosk"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="open-kiosk-link"
            className="flex items-center justify-between gap-2 px-4 py-3 rounded-full text-sm bg-secondary hover:bg-muted transition-colors"
          >
            <span>Open kiosk</span>
            <ExternalLink className="w-4 h-4" aria-hidden="true" />
          </a>
        </div>

        <div className="p-4 border-t border-border">
          <div className="px-2 mb-3">
            <div className="text-xs text-muted-foreground">Signed in as</div>
            <div className="text-sm font-medium truncate" data-testid="signed-in-email">
              {user?.email}
            </div>
          </div>
          <Button
            onClick={doLogout}
            data-testid="logout-btn"
            variant="ghost"
            className="w-full justify-start rounded-full text-foreground/70 hover:text-foreground"
          >
            <LogOut className="w-4 h-4 mr-2" aria-hidden="true" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Mobile top nav */}
      <div className="md:hidden fixed top-0 inset-x-0 z-10 bg-white/90 backdrop-blur border-b border-border px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Logo variant="mark" className="w-7 h-7" />
          <div className="font-serif text-lg">Royal Wow</div>
        </div>
        <Button size="sm" variant="ghost" onClick={doLogout} data-testid="logout-btn-mobile">
          <LogOut className="w-4 h-4" aria-hidden="true" />
        </Button>
      </div>

      {/* Content */}
      <main className="flex-1 md:pl-0 pt-16 md:pt-0 overflow-x-auto">
        <div className="p-4 sm:p-6 md:p-12 pb-24 md:pb-6 max-w-7xl">
          <Outlet />
        </div>

        {/* Mobile bottom nav — horizontally scrollable so 11+ items stay readable */}
        <div className="md:hidden fixed bottom-0 inset-x-0 z-10 bg-white border-t border-border">
          <div className="flex overflow-x-auto no-scrollbar snap-x snap-mandatory">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`${n.testId}-mobile`}
                className={({ isActive }) =>
                  `snap-start flex-shrink-0 min-w-[72px] flex flex-col items-center gap-1 py-2.5 px-3 text-[10px] ${
                    isActive ? "text-primary font-medium" : "text-muted-foreground"
                  }`
                }
              >
                <n.icon className="w-5 h-5" aria-hidden="true" />
                <span className="whitespace-nowrap">{n.label}</span>
              </NavLink>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
